'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Collapse,
  Dialog,
  DialogContent,
  Divider,
  Fade,
  InputAdornment,
  Paper,
  Slide,
  Typography,
  useScrollTrigger,
} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import { TransitionProps } from '@mui/material/transitions';
import { Box, Stack } from '@mui/system';
import { isEmpty, startCase } from 'lodash';
import { useQueryState } from 'nuqs';
import posthog from 'posthog-js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { sendPhoneVerificationCode, verifyPhoneCode } from 'src/api/auth';
import { useGetIsSubscribed, useSubscribeToOrganizer } from 'src/api/hooks/organizer';
import { useFindUserExist, useGetBioPage } from 'src/api/organizer';
import PageNotFoundIllustration from 'src/assets/illustrations/page-not-found-illustration';
import { useAuthContext } from 'src/auth/hooks';
import { RHFCode, RHFTextField } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import Logo from 'src/components/logo';
import { useDebounce } from 'src/hooks/use-debounce';
import { useOffSetTop } from 'src/hooks/use-off-set-top';
import { useResponsive } from 'src/hooks/use-responsive';
import { HeaderShadow } from 'src/layouts/_common';
import { useRouter } from 'src/routes/hooks';
import { bgBlur } from 'src/theme/css';
import { SubEntityType, SubscriptionType } from 'src/types';
// @ts-nocheck
// @ts-ignore
import * as Yup from 'yup';

import EventDetailsHeader from '../event-details-header';
import MusicList from '../music-list';
import PastEvent from '../past-events';
import UpcomingEvents from '../upcoming-events';
import VideoList from '../video-list';

type Props = {
  pseudo: string;
};
function convertVhToPx(vh: number) {
  if (typeof window !== 'undefined') {
    return vh * (window.innerHeight / 100);
  }
  return 0; // Fallback for SSR
}

interface ScrollProps {
  window?: () => Window;
  children: React.ReactElement;
  manualUp: string;
}

const Transition = React.forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
  ) => <Slide direction="up" ref={ref} {...props} />
);

function HideOnScroll(props: ScrollProps) {
  const { children, window, manualUp } = props;

  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
    disableHysteresis: true,
    threshold: 100,
  });

  const manualUpBoolean = manualUp !== 'false';

  return (
    <Slide appear={false} direction="up" in={manualUp ? Boolean(manualUpBoolean) : !trigger}>
      {children}
    </Slide>
  );
}

const SubscribeSchema = Yup.object().shape({
  code: Yup.string(),
  email: Yup.string()
    .required('Email is required')
    .email('Email must be a valid email address')
    .test('is-valid-email', 'Email must be a valid email address', (value) => {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(value);
    }),
});

export default function BioDetailView({ pseudo }: Props) {
  const theme = useTheme();
  const router = useRouter();
  const [showEmail, setShowEmail] = useState(false);
  const [showPhoneCode, setShowPhoneCode] = useState(true);
  const mdUp = useResponsive('up', 'md');
  const { authenticated, user } = useAuthContext();
  const { mutateAsync: subscribe, isPending: isSubmitting } = useSubscribeToOrganizer();

  const defaultValues = useMemo(
    () => ({
      email: '',
      code: '',
    }),
    []
  );

  const methods = useForm({
    resolver: yupResolver(SubscribeSchema),
    defaultValues,
    mode: 'onBlur',
  });
  const {
    watch,
    setError,
    trigger,
    reset,
    handleSubmit,
    formState: { isDirty, isValid },
  } = methods;

  const currentEmail = watch('email');
  const currentSubscriberEmail =
    (user?.email as string) ||
    currentEmail ||
    (typeof window !== 'undefined' && localStorage.getItem('subscribed_email')) ||
    '';

  const { data: alreadySubscribed } = useGetIsSubscribed(currentSubscriberEmail ?? '', pseudo);

  const currentCode = watch('code');

  const [openSubscribe, setOpenSubscribe] = useQueryState('openSubscribe');

  const debouncedSearchTerm = useDebounce(currentEmail, 500);
  const {
    data: isUserExist,
    isLoading: isUserLoading,
    mutate,
  } = useFindUserExist(debouncedSearchTerm);

  const onClosePopup = useCallback(() => {
    setOpenSubscribe('false');
    reset();
    mutate(false);
    setShowEmail(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: orgPage, isLoading } = useGetBioPage(pseudo);
  const isSubscribed = useMemo(() => {
    if (typeof window !== 'undefined') {
      return Boolean(localStorage.getItem(pseudo));
    }
    return false;
  }, [pseudo]);

  const offsetTop = useOffSetTop(2);
  const offsetTitle = useOffSetTop(convertVhToPx(27));

  const onSendVerify = useCallback(async () => {
    await sendPhoneVerificationCode({ email: currentEmail });
    SubscribeSchema.shape({
      code: Yup.string().min(6, 'Code must be at least 6 characters').required('Code is required'),
    });
  }, [currentEmail]);

  const onVerifyCode = useCallback(async () => {
    try {
      if (currentCode) {
        await verifyPhoneCode({ email: currentEmail, code: currentCode });
        setShowPhoneCode(false);
      }
    } catch (e) {
      setError('code', { type: 'custom', message: 'Wrong code' });
    }
  }, [currentCode, currentEmail, setError]);

  useEffect(() => {
    if (isUserExist && !authenticated) {
      onSendVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserExist]);

  useEffect(() => {
    const validateCode = async () => {
      if (isUserExist) {
        await trigger('code');
      }
    };

    if (currentCode && isUserExist) {
      validateCode();
    }
  }, [currentCode, isUserExist, trigger]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (isUserExist) {
        await onVerifyCode();
        await subscribe({
          email: data.email,
          entityId: orgPage?.id || 0,
          entityType: SubEntityType.Organization,
          subscriptionType: SubscriptionType.Organizer,
          locations: [{ placeId: 'ChIJW6T5-yMbyUwRLWIrtNTBSaw' }],
        });
        localStorage.setItem('subscribed_email', data.email);
      } else {
        await subscribe({
          email: data.email,
          entityId: orgPage?.id || 0,
          entityType: SubEntityType.Organization,
          subscriptionType: SubscriptionType.Organizer,
          locations: [{ placeId: 'ChIJW6T5-yMbyUwRLWIrtNTBSaw' }],
        });
        localStorage.setItem('subscribed_email', data.email);
      }
      setShowPhoneCode(true);
    } catch (error) {
      console.error('subscribe error ', error);
    }
  });

  const onSubmitAuth = async () => {
    try {
      if (authenticated) {
        await subscribe({
          email: user?.email as string,
          entityId: orgPage?.id || 0,
          entityType: SubEntityType.Organization,
          subscriptionType: SubscriptionType.Organizer,
          locations: [{ placeId: 'ChIJW6T5-yMbyUwRLWIrtNTBSaw' }],
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const onNavigateBack = () => {
    if (window.history?.length) {
      router.push('/');
    } else {
      router.replace('trybe.fm');
    }
  };

  const isButtonDisable = useMemo(() => {
    if (isUserExist && (currentCode?.length ?? 0) < 6) {
      return true;
    }
    if (Boolean(openSubscribe) && showEmail && !isValid) {
      return true;
    }
    return false;
  }, [openSubscribe, showEmail, isValid, isUserExist, currentCode]);

  return (
    <Container
      sx={{
        p: 0,
        backgroundColor: '#09090B',
        overflow: 'hidden',
        ...(!orgPage && {
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
        }),
      }}
      maxWidth="md"
    >
      <AppBar>
        <Toolbar
          disableGutters
          sx={{
            height: {
              xs: 3,
              md: 3,
            },
            transition: theme.transitions.create(['height'], {
              easing: theme.transitions.easing.easeInOut,
              duration: theme.transitions.duration.shorter,
            }),
            ...(offsetTop && {
              ...bgBlur({
                color: theme.palette.background.default,
              }),
              height: {
                md: 3,
              },
            }),
          }}
        >
          <Container
            sx={{
              height: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Stack direction="row" alignItems="center">
              <IconButton onClick={() => onNavigateBack()}>
                <Iconify icon="ion:arrow-back-outline" color="#fff" />
              </IconButton>
              {offsetTitle && (
                <Fade in>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Image
                      src={orgPage?.logo?.url}
                      width={35}
                      height={35}
                      sx={{ borderRadius: 1 }}
                    />
                    <Typography>{startCase(orgPage?.name)}</Typography>
                  </Stack>
                </Fade>
              )}
            </Stack>
            {offsetTop && alreadySubscribed ? (
              <LoadingButton
                variant="outlined"
                disabled={Boolean(isButtonDisable)}
                endIcon={<Iconify icon="line-md:bell-alert-loop" />}
                sx={{
                  fontSize: '15px',
                  py: 1,
                  px: 2,
                }}
              >
                Subscribed
              </LoadingButton>
            ) : (
              <IconButton
                onClick={() => {
                  setOpenSubscribe('true');
                  posthog.capture('bio_subscribed', {
                    entityId: orgPage?.id,
                    email: user?.email,
                    entityType: SubEntityType.Organization,
                    // to be replaced by correct locations
                    locations: [{ placeId: 'ChIJW6T5-yMbyUwRLWIrtNTBSaw' }],
                    subscriptionType: SubscriptionType.Organizer,
                  });
                }}
              >
                <Iconify icon="line-md:bell-alert-loop" color="#fff" />
              </IconButton>
            )}
          </Container>
        </Toolbar>

        {offsetTop && <HeaderShadow />}
      </AppBar>
      {!orgPage && !isLoading && (
        <Stack flex={1} sx={{ px: 7 }} alignItems="center" justifyContent="center" gap={7}>
          <Stack gap={1} alignItems="center" justifyContent="center">
            <Typography variant="h4">The Page You Looking For</Typography>
            <Typography variant="h4">Does not exist</Typography>
          </Stack>
          <PageNotFoundIllustration height="none" />
        </Stack>
      )}
      {orgPage && <EventDetailsHeader bio={orgPage} />}
      {orgPage?.pageSetting?.upcomingEvents && (
        <Box sx={{ px: 3 }} id="Upcoming">
          <UpcomingEvents
            title="Upcoming"
            isSubscribed={alreadySubscribed ?? false}
            list={orgPage?.pageSetting?.upcomingEvents}
            isLoading={false}
          />
        </Box>
      )}
      {orgPage?.pageSetting?.selectedEvents && (
        <Box sx={{ px: 3 }} id="Popular">
          <PastEvent
            title="Popular"
            list={orgPage?.pageSetting?.selectedEvents}
            isLoading={false}
          />
        </Box>
      )}
      {!isEmpty(orgPage?.pageSetting?.videoLinks) && (
        <Box sx={{ px: 3, mt: 2, maxHeight: { md: 500, sm: 350, xs: 270 } }} id="Video">
          {orgPage?.pageSetting?.videoLinks && (
            <VideoList title="Video" list={orgPage?.pageSetting?.videoLinks} isLoading={false} />
          )}
        </Box>
      )}
      {!isEmpty(orgPage?.pageSetting?.playlistLinks) && (
        <Box sx={{ px: 3, py: 2 }} id="Music">
          {orgPage?.pageSetting?.playlistLinks && (
            <MusicList
              title="Music"
              list={orgPage?.pageSetting?.playlistLinks || []}
              isLoading={false}
            />
          )}
        </Box>
      )}

      <Divider sx={{ width: '100%', mt: 3 }} />
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          direction: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Button href="/">Discover Events</Button>
        <Logo />
        <Button
          href="https://nest.trybe.fm"
          target="_blank"
          rel="noopener noreferrer"
          variant="text"
          onClick={() => posthog.capture('bio_upsell_clicked')}
        >
          Get your own page
        </Button>
      </Box>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        {!mdUp && (
          <Dialog
            open={showEmail}
            fullScreen
            TransitionComponent={Transition}
            keepMounted
            onClose={onClosePopup}
          >
            <AppBar sx={{ position: 'sticky', backgroundColor: '#000' }}>
              <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <IconButton edge="start" color="inherit" onClick={onClosePopup} aria-label="close">
                  <Iconify icon="tdesign:close-circle-filled" />
                </IconButton>
                <Typography variant="h4">Subscribe</Typography>
                <Stack />
              </Toolbar>
            </AppBar>
            <DialogContent sx={{ pt: 2 }}>
              <Stack gap={1} alignItems="flex-end">
                <Stack gap={1} sx={{ borderRadius: 2, mb: 2, border: '1px solid white', p: 2 }}>
                  <Stack direction="row" gap={1}>
                    <Iconify icon="solar:mailbox-outline" />
                    <Typography>{`Stay in the Loop with ${orgPage?.name}`}</Typography>
                  </Stack>
                  <Typography variant="caption" color="#919eabcc">
                    Be the first to know about upcoming events, exclusive offers, and early bird
                    tickets!
                  </Typography>
                </Stack>
                <Collapse in={showEmail && !isSubscribed} sx={{ width: '100%' }}>
                  <RHFTextField
                    fullWidth
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    // onChange={() => trigger('email')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {isUserLoading ? (
                            <Iconify
                              icon="line-md:loading-loop"
                              width={22}
                              height={22}
                              color="yellow"
                            />
                          ) : (
                            isUserExist && (
                              <Iconify
                                icon="line-md:confirm"
                                width={22}
                                height={22}
                                color="green"
                              />
                            )
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Collapse
                    in={showPhoneCode && isUserExist && !isSubscribed}
                    sx={{ width: '100%', mb: 1 }}
                  >
                    <Stack gap={1}>
                      <Stack spacing={1} sx={{ my: 2 }}>
                        <Stack gap={1} direction="row" alignItems="center">
                          <Iconify icon="fluent-mdl2:mobile-selected" width={20} />
                          <Typography variant="h3">Check your phone</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {`We've sent you the confirmation code. Please enter it below.`}
                        </Typography>
                      </Stack>

                      <RHFCode name="code" />
                    </Stack>
                  </Collapse>
                </Collapse>
                <LoadingButton
                  variant={alreadySubscribed ? 'outlined' : 'contained'}
                  disabled={Boolean(isButtonDisable)}
                  loading={isSubmitting}
                  onClick={() =>
                    // eslint-disable-next-line no-nested-ternary
                    authenticated ? onSubmitAuth() : isDirty ? onSubmit() : setShowEmail(true)
                  }
                  endIcon={<Iconify icon="line-md:bell-alert-loop" />}
                  sx={{
                    fontSize: '15px',
                    py: 1,
                    px: 2,
                    mt: 1,
                  }}
                >
                  {alreadySubscribed ? 'Subscribed' : 'Notify Me'}
                </LoadingButton>
              </Stack>
            </DialogContent>
          </Dialog>
        )}
        {orgPage && !isSubscribed && (
          <DialogOrSlide
            openSubscribe={openSubscribe ?? ''}
            isMdUp={mdUp}
            handleClose={onClosePopup}
          >
            <Stack
              alignItems="flex-end"
              justifyContent="space-between"
              gap={1}
              sx={{
                position: mdUp ? null : 'absolute',
                bottom: mdUp ? null : -5,
                zIndex: 20,
                p: 2,
                pb: 5,
                width: '100%',
                borderTopLeftRadius: 20,
                borderTopRightRadius: 20,
                backgroundColor: mdUp ? 'transparent' : '#161616',
              }}
            >
              <Stack alignItems="flex-end" justifyContent="space-between" gap={1} width="100%">
                {mdUp && (
                  <Stack gap={1} alignItems="flex-end">
                    <IconButton onClick={() => onClosePopup()}>
                      <Iconify icon="line-md:menu-to-close-alt-transition" color="#fff" />
                    </IconButton>
                    <Collapse in={showEmail && !isSubscribed} sx={{ width: '100%' }}>
                      <RHFTextField
                        fullWidth
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        // onChange={() => trigger('email')}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {isUserLoading ? (
                                <Iconify
                                  icon="line-md:loading-loop"
                                  width={22}
                                  height={22}
                                  color="yellow"
                                />
                              ) : (
                                isUserExist && (
                                  <Iconify
                                    icon="line-md:confirm"
                                    width={22}
                                    height={22}
                                    color="green"
                                  />
                                )
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Collapse
                        in={showPhoneCode && isUserExist && !isSubscribed}
                        sx={{ width: '100%', mt: 2 }}
                      >
                        <RHFCode name="code" />
                      </Collapse>
                    </Collapse>
                  </Stack>
                )}
                <Stack width="100%">
                  <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                    {`Subscribe to ${orgPage?.name}`}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  gap={1}
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Typography sx={{ fontSize: '15px' }}>
                    Get the latest event&apos;s updates
                  </Typography>

                  <LoadingButton
                    variant={alreadySubscribed ? 'outlined' : 'contained'}
                    disabled={Boolean(isButtonDisable)}
                    loading={isSubmitting}
                    onClick={() =>
                      // eslint-disable-next-line no-nested-ternary
                      authenticated ? onSubmitAuth() : isDirty ? onSubmit() : setShowEmail(true)
                    }
                    endIcon={<Iconify icon="line-md:bell-alert-loop" />}
                    sx={{
                      fontSize: '15px',
                      py: 1,
                      px: 2,
                    }}
                  >
                    {alreadySubscribed ? 'Subscribed' : 'Notify Me'}
                  </LoadingButton>
                </Stack>
              </Stack>
            </Stack>
          </DialogOrSlide>
        )}
      </FormProvider>
    </Container>
  );
}

interface IDialogOrSlide {
  openSubscribe: string;
  children: React.ReactElement;
  isMdUp: boolean;
  handleClose?: () => void;
}

const DialogOrSlide = ({ children, isMdUp, openSubscribe, handleClose }: IDialogOrSlide) =>
  isMdUp ? (
    <Dialog
      open={openSubscribe === 'true'}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogContent sx={{ overflowY: 'auto', maxHeight: '80vh', p: 0 }}>{children}</DialogContent>
    </Dialog>
  ) : (
    <HideOnScroll manualUp={openSubscribe}>
      <Paper sx={{ position: 'fixed', bottom: -3, left: 0, right: 0, zIndex: 10 }} elevation={3}>
        <Slide direction="up" in mountOnEnter unmountOnExit>
          {children}
        </Slide>
      </Paper>
    </HideOnScroll>
  );
